/**
 * Candidate-facing Pre-onboarding Portal — public, magic-link gated.
 *
 * Route: `/preboarding/:token`. No login required. The token alone is the
 * credential. Renders: welcome card with countdown to joining, buddy intro,
 * checklist (candidate-owned items only — file upload + free-text values),
 * Day-1 schedule, and a quick FAQ. The candidate marks items done; HR/IT/
 * Facilities items are read-only here (those owners flip them from the HR
 * cockpit).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { CheckCircle, Clock, Calendar, Confetti, ChatCircleText, Upload, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }); } catch { return iso; } };
const daysUntil = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date();
    today.setHours(0,0,0,0);
    d.setHours(0,0,0,0);
    return Math.round((d - today) / 86400000);
  } catch { return null; }
};

export default function CandidatePreboarding() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/preboarding/portal/${token}`);
      setDoc(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "This link is invalid or has expired.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  if (loading) {
    return <Shell>
      <div className="text-center text-zinc-500 py-20">Loading your portal…</div>
    </Shell>;
  }
  if (error) {
    return <Shell>
      <Card className="p-8 text-center">
        <Clock size={48} className="mx-auto text-rose-400 mb-3" />
        <h2 className="text-xl font-bold text-zinc-900">{error}</h2>
        <p className="text-sm text-zinc-600 mt-2">Please ask your HR contact to send a fresh link.</p>
      </Card>
    </Shell>;
  }

  const du = doc.joining_date ? daysUntil(doc.joining_date) : null;
  const done = (doc.checklist || []).filter((c) => c.status === "done").length;
  const total = (doc.checklist || []).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const candItems = (doc.checklist || []).filter((c) => c.owner === "candidate");
  const otherItems = (doc.checklist || []).filter((c) => c.owner !== "candidate");

  return (
    <Shell>
      {/* Hero */}
      <Card className="p-8 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border-indigo-100" data-testid="pb-public-hero">
        <Confetti size={36} className="text-indigo-500 mb-3" />
        <h1 className="text-3xl font-bold text-zinc-900">Welcome, {(doc.candidate_name || "").split(" ")[0]}!</h1>
        <p className="text-zinc-700 mt-2 text-base">
          You&apos;ve accepted the offer for <strong>{doc.requisition_title}</strong> at <strong>{doc.company_name}</strong>. We&apos;re thrilled to have you on board.
        </p>
        {doc.joining_date && (
          <div className="mt-4 flex flex-wrap gap-4">
            <div className="bg-white border border-indigo-200 rounded-lg px-4 py-3">
              <div className="text-xs uppercase tracking-wider text-indigo-700">Joining date</div>
              <div className="text-lg font-bold text-zinc-900">{fmtDate(doc.joining_date)}</div>
            </div>
            {du != null && (
              <div className="bg-emerald-100 border border-emerald-300 rounded-lg px-4 py-3">
                <div className="text-xs uppercase tracking-wider text-emerald-800">Countdown</div>
                <div className="text-lg font-bold text-emerald-900">
                  {du < 0 ? "Welcome aboard!" : du === 0 ? "Today is the day!" : `${du} day${du === 1 ? "" : "s"} to go`}
                </div>
              </div>
            )}
            {doc.reserved_employee_code && (
              <div className="bg-white border border-zinc-200 rounded-lg px-4 py-3">
                <div className="text-xs uppercase tracking-wider text-zinc-500">Your employee ID</div>
                <div className="text-lg font-bold text-zinc-900 font-mono">{doc.reserved_employee_code}</div>
              </div>
            )}
          </div>
        )}
        {doc.welcome_message_md && (
          <Card className="mt-5 p-4 bg-white border-zinc-200">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">A note for you</div>
            <p className="text-zinc-800 whitespace-pre-line">{doc.welcome_message_md}</p>
          </Card>
        )}
      </Card>

      {/* Progress bar */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-zinc-900">Your checklist progress</h2>
          <span className="text-sm text-zinc-600">{done} / {total} complete</span>
        </div>
        <div className="w-full bg-zinc-100 rounded-full h-2">
          <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </Card>

      {/* Buddy intro */}
      {doc.buddy_name && (
        <Card className="p-5" data-testid="pb-public-buddy">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700">
              <UserCircle size={28} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Your onboarding buddy</div>
              <h3 className="text-lg font-semibold text-zinc-900">{doc.buddy_name}</h3>
              {doc.buddy_intro && <p className="text-sm text-zinc-700 mt-1">{doc.buddy_intro}</p>}
              {doc.buddy_email && <a href={`mailto:${doc.buddy_email}`} className="text-sm text-indigo-600 hover:underline">{doc.buddy_email}</a>}
            </div>
          </div>
        </Card>
      )}

      {/* Candidate checklist */}
      <Card className="p-5" data-testid="pb-public-checklist">
        <h2 className="font-semibold text-zinc-900 mb-3">Your to-dos</h2>
        <p className="text-sm text-zinc-600 mb-4">Complete these so we can have everything ready for your first day.</p>
        <ul className="space-y-2">
          {candItems.map((it) => (
            <ChecklistRow key={it.id} item={it} token={token} onChanged={load} />
          ))}
        </ul>
      </Card>

      {/* Day 1 schedule */}
      {(doc.day_one_schedule || []).length > 0 && (
        <Card className="p-5" data-testid="pb-public-schedule">
          <h2 className="font-semibold text-zinc-900 mb-3">Your Day 1</h2>
          <ul className="space-y-2">
            {(doc.day_one_schedule || []).slice().sort((a,b)=>(a.time||"").localeCompare(b.time||"")).map((s) => (
              <li key={s.id} className="flex items-start gap-3 py-2 border-b last:border-0 border-zinc-100">
                <div className="text-sm font-mono font-semibold text-indigo-700 w-14 shrink-0">{s.time}</div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-zinc-900">{s.title}</div>
                  <div className="text-xs text-zinc-500">{[s.location, s.owner_name].filter(Boolean).join(" · ")}</div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* What we'll handle */}
      {otherItems.length > 0 && (
        <Card className="p-5" data-testid="pb-public-other">
          <h2 className="font-semibold text-zinc-900 mb-2">What we&apos;ll have ready for you</h2>
          <p className="text-sm text-zinc-600 mb-3">These are handled by our team — no action needed on your end.</p>
          <ul className="space-y-1">
            {otherItems.map((it) => (
              <li key={it.id} className="flex items-center gap-2 text-sm py-1">
                {it.status === "done"
                  ? <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                  : <Clock size={16} className="text-zinc-400" />}
                <span className={it.status === "done" ? "text-zinc-500 line-through" : "text-zinc-700"}>
                  {it.label}
                </span>
                <Badge variant="outline" className="text-[10px] ml-1">{it.owner}</Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* FAQ */}
      <Card className="p-5">
        <h2 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
          <ChatCircleText size={20} className="text-indigo-600" /> Frequently asked
        </h2>
        <div className="space-y-3 text-sm">
          <FAQ q="What time should I reach on Day 1?" a="Plan to be at the office by 9:00 AM. Your buddy will be at reception to greet you." />
          <FAQ q="What should I wear?" a="Wear what you'd normally wear to work — we don't have a strict dress code." />
          <FAQ q="What documents should I bring on Day 1?" a="Bring the originals of all the documents you've uploaded here (PAN, Aadhaar, education and experience certificates). HR will verify and return them." />
          <FAQ q="Who do I contact if something's not clear?" a={`Your buddy ${doc.buddy_name || "(to be assigned)"} or reply to any email from us — we'll respond within a working day.`} />
        </div>
      </Card>
    </Shell>
  );
}

function ChecklistRow({ item, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [value, setValue] = useState("");
  const [file, setFile] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const needsFile = ["docs", "banking"].includes(item.category);
  const needsValue = !needsFile;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {};
      if (file) {
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        body.evidence_b64 = b64;
        body.evidence_filename = file.name;
      }
      if (value) body.value = value;
      if (!body.evidence_b64 && !body.value) {
        toast.error(needsFile ? "Please attach a file." : "Please enter a value.");
        setBusy(false);
        return;
      }
      await axios.post(`${API}/preboarding/portal/${token}/checklist/${item.id}/complete`, body);
      toast.success("Saved!");
      setExpanded(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save. Try again.");
    } finally { setBusy(false); }
  };

  if (item.status === "done") {
    return (
      <li className="flex items-center justify-between py-2 bg-emerald-50 rounded px-3">
        <div className="flex items-center gap-2">
          <CheckCircle size={18} weight="fill" className="text-emerald-500" />
          <span className="text-sm text-zinc-700 line-through">{item.label}</span>
        </div>
        {item.evidence_filename && <a href={item.evidence_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600">{item.evidence_filename}</a>}
      </li>
    );
  }

  return (
    <li className="border border-zinc-200 rounded p-3" data-testid={`pb-public-item-${item.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-zinc-300 rounded shrink-0" />
          <span className="text-sm text-zinc-900">{item.label}</span>
          {item.required && <Badge variant="outline" className="text-[10px]">required</Badge>}
        </div>
        <Button size="sm" variant={expanded ? "outline" : "default"} onClick={() => setExpanded(!expanded)} data-testid={`pb-public-toggle-${item.id}`}>
          {expanded ? "Cancel" : (needsFile ? "Upload" : "Add details")}
        </Button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2">
          {needsFile && (
            <div>
              <Label className="text-xs">Upload file (PDF/JPG/PNG, ≤ 5 MB)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`pb-public-file-${item.id}`} />
            </div>
          )}
          {needsValue && (
            <div>
              <Label className="text-xs">Your response</Label>
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g., L (for T-shirt size)" data-testid={`pb-public-value-${item.id}`} />
            </div>
          )}
          <Button size="sm" onClick={submit} disabled={busy} data-testid={`pb-public-submit-${item.id}`}>
            {busy ? "Saving…" : "Submit"}
          </Button>
        </div>
      )}
    </li>
  );
}

function FAQ({ q, a }) {
  return (
    <div>
      <div className="font-medium text-zinc-900">{q}</div>
      <div className="text-zinc-600 mt-0.5">{a}</div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">
        <div className="text-center mb-4">
          <div className="inline-block text-2xl font-bold tracking-tight text-zinc-900">SaarOne</div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Pre-onboarding Portal</div>
        </div>
        {children}
        <div className="text-center text-xs text-zinc-400 mt-8">
          Need help? Reply to any email from us — we respond within a working day.
        </div>
      </div>
    </div>
  );
}
